import { useEffect, useState } from 'react';
import { Admin, Me, type AdminUserRow, type UserRole, type UserSummary } from '../../lib/api';
import { toneClass, roleTone } from '../../lib/ticket-ui';

export function UsersAdminPage() {
  const [users, setUsers] = useState<AdminUserRow[]>([]);
  const [me, setMe] = useState<UserSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const [u, m] = await Promise.all([Admin.listUsers(), Me.get()]);
      setUsers(u.users);
      setMe(m.user);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, []);

  async function update(id: string, body: { role?: UserRole; active?: boolean }) {
    setError(null);
    try {
      await Admin.updateUser(id, body);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Update failed');
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-lg font-semibold text-slate-900">Users</h1>
        <p className="text-sm text-slate-500">
          Anyone with a <code className="font-mono">@just1label.com</code> Google account can sign in. New
          sign-ins land as <strong>STAFF</strong> by default — promote here.
        </p>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      {loading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : (
        <>
          {/* Desktop: table */}
          <div className="hidden md:block bg-white border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-left">
                <tr>
                  <th className="px-3 py-2">Name</th>
                  <th className="px-3 py-2">Email</th>
                  <th className="px-3 py-2 w-32">Role</th>
                  <th className="px-3 py-2 w-24">Active</th>
                  <th className="px-3 py-2 w-32">Signed in?</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const isMe = me?.id === u.id;
                  return (
                    <tr key={u.id} className="border-t border-slate-100">
                      <td className="px-3 py-2 font-medium text-slate-900">
                        {u.name}
                        {isMe && <span className="ml-2 text-xs text-slate-500">(you)</span>}
                      </td>
                      <td className="px-3 py-2 text-slate-600">{u.email}</td>
                      <td className="px-3 py-2">
                        <select
                          value={u.role}
                          disabled={isMe}
                          onChange={(e) => void update(u.id, { role: e.target.value as UserRole })}
                          className={`text-xs px-2 py-1 rounded font-medium border ${toneClass[roleTone[u.role]]} ${isMe ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                        >
                          <option value="ADMIN">ADMIN</option>
                          <option value="DEVELOPER">DEVELOPER</option>
                          <option value="STAFF">STAFF</option>
                        </select>
                      </td>
                      <td className="px-3 py-2">
                        <label className={`inline-flex items-center gap-2 ${isMe ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}>
                          <input
                            type="checkbox"
                            checked={u.active}
                            disabled={isMe}
                            onChange={(e) => void update(u.id, { active: e.target.checked })}
                          />
                          <span className="text-xs text-slate-600">{u.active ? 'Active' : 'Disabled'}</span>
                        </label>
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-500">
                        {u.hasSignedIn ? 'Yes' : 'Never (pre-seeded)'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile: cards */}
          <div className="md:hidden space-y-2">
            {users.map((u) => {
              const isMe = me?.id === u.id;
              return (
                <div key={u.id} className="bg-white border border-slate-200 rounded-lg p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-slate-900">
                        {u.name}{isMe && <span className="ml-2 text-xs text-slate-500">(you)</span>}
                      </div>
                      <div className="text-xs text-slate-500">{u.email}</div>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-3 text-xs">
                    <select
                      value={u.role}
                      disabled={isMe}
                      onChange={(e) => void update(u.id, { role: e.target.value as UserRole })}
                      className={`px-2 py-1 rounded font-medium border ${toneClass[roleTone[u.role]]} ${isMe ? 'opacity-60' : ''}`}
                    >
                      <option value="ADMIN">ADMIN</option>
                      <option value="DEVELOPER">DEVELOPER</option>
                      <option value="STAFF">STAFF</option>
                    </select>
                    <label className="inline-flex items-center gap-1.5">
                      <input
                        type="checkbox"
                        checked={u.active}
                        disabled={isMe}
                        onChange={(e) => void update(u.id, { active: e.target.checked })}
                      />
                      {u.active ? 'Active' : 'Disabled'}
                    </label>
                    <span className="text-slate-500">{u.hasSignedIn ? 'Signed in' : 'Pre-seeded'}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
